import { createTheme, type ThemeOptions } from '@mui/material/styles';
// Registers the MuiDataGrid key on the theme's `components` (types only).
import type {} from '@mui/x-data-grid/themeAugmentation';

/**
 * MUI theme tuned to MATCH the legacy `assets/css/payroll-theme.css` exactly.
 * Variable mapping (legacy → MUI):
 *   --bg #f4f2fb               → background.default        (page bg, soft lavender-grey)
 *   --surface #ffffff          → background.paper          (card bg)
 *   --surface2 #f7f9fc         → grey.50                   (alt surface)
 *   --s2 #f3f7fb               → grey.100                  (panel/header bg)
 *   --s3 #e8edf2               → grey.200
 *   --border #e1e5e8           → divider
 *   --ink #333333              → text.primary
 *   --ink2 #666666             → text.secondary
 *   --ink3 #999999             → text.disabled
 *   --accent #108ae6           → primary.main              (the trademark blue)
 *   --acc2 #e8f4fd             → primary.light             (accent fill)
 *   --green #1A7845            → success.main
 *   --red #C22C2C              → error.main
 *   --amber #8C5100            → warning.main
 *   --r 8px / --rl 12px        → shape.borderRadius (8)
 *   font Lato                  → typography.fontFamily
 *
 * The blue sidebar (#108ae6 with white nav items) is rendered via direct
 * styles in <Sidebar> rather than the theme; same for the topbar.
 */
/** The options behind `theme` — BrandedThemeProvider rebuilds from them. */
export const themeOptions: ThemeOptions = {
  palette: {
    primary: {
      main:        '#108ae6',
      light:       '#e8f4fd',
      dark:        '#0d79c7',
      contrastText: '#ffffff',
    },
    secondary: { main: '#666666' },
    success:   { main: '#1bcb84', light: '#e9fbf3' },
    error:     { main: '#dc5640', light: '#faece9' },
    warning:   { main: '#f1b44c', light: '#fef9ed' },
    info:      { main: '#0d7cc7', light: '#e8f4fd' },
    background: {
      default: '#f3f3f9',   // page bg — wireframe --bg
      paper:   '#ffffff',
    },
    text: {
      primary:   '#333333',
      secondary: '#666666',
      disabled:  '#999999',
    },
    divider: '#e1e5e8',
    grey: {
      50:  '#f7f9fc',
      100: '#f3f7fb',
      200: '#e8edf2',
      300: '#d7dfe7',
      400: '#c7ced6',
      500: '#999999',
      600: '#8a8378',
      700: '#666666',
      800: '#4a4742',
      900: '#333333',
    },
  },
  typography: {
    fontFamily: '"Lato", "Plus Jakarta Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: 13,
    h1: { fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', color: '#333333' },
    h2: { fontSize: 18, fontWeight: 700, color: '#333333' },
    h3: { fontSize: 15, fontWeight: 700, color: '#333333' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    body1: { fontSize: 13 },
    body2: { fontSize: 12.5 },
    caption: { fontSize: 11.5 },
  },
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    '0 1px 3px rgba(24, 28, 44, 0.07)',
    '0 1px 3px rgba(24, 28, 44, 0.07), 0 4px 14px rgba(24, 28, 44, 0.04)',
    ...Array(22).fill('0 4px 18px rgba(24, 28, 44, 0.06)'),
  ] as never,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Keep html, body and #root at exact viewport dimensions and never
        // grow — the AppShell handles all internal scrolling. This prevents
        // any double-scrollbar / horizontal-overflow surprises.
        html:  { height: '100%', overflow: 'hidden' },
        body:  { height: '100%', margin: 0, backgroundColor: '#f3f3f9', overflow: 'hidden' },
        '#root': { height: '100%' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        // .section-card / .table-wrap — 12px, not the global 8px
        root: { backgroundImage: 'none', borderRadius: 12 },
        outlined: { borderColor: '#e1e5e8' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { borderRadius: 12, border: '1px solid #e1e5e8' } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // .btn — 36px tall, 8px radius, 14px/600 label
        root: {
          borderRadius: 8,
          minHeight: 34,
          padding: '0 16px',
          fontSize: 14,
          fontWeight: 600,
        },
        // .btn-sm
        sizeSmall: { minHeight: 30, padding: '0 12px', fontSize: 13, borderRadius: 6 },
        outlined: { borderColor: '#c7ced6' },
        // Tighten the icon↔label gap (MUI's default startIcon margin is too wide).
        startIcon: { marginRight: 6, marginLeft: -2 },
        endIcon: { marginLeft: 6, marginRight: -2 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        // position:relative so the top-right close (X) button anchors here;
        // pad the right so the title text never runs under it.
        root: { position: 'relative', paddingRight: 52 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40 },
        indicator: { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: '#108ae6' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, fontSize: 13.5, minHeight: 40,
          '&.Mui-selected': { color: '#108ae6' },
        },
      },
    },
    MuiTextField: {
      // Always float the label (notched outline) so every field reads the same
      // whether it's empty or filled — otherwise filled fields float the label
      // while empty ones centre it, and a form looks uneven row to row. Also
      // sidesteps the label-overlap glitch when values arrive asynchronously.
      defaultProps: {
        size: 'small', variant: 'outlined', fullWidth: true,
        InputLabelProps: { shrink: true },
      },
    },
    MuiSelect:    { defaultProps: { size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { fontSize: 13, backgroundColor: '#fff' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // .data-table thead th — tinted with the accent wash, not plain grey.
        head: {
          fontWeight: 600,
          background: '#e8f4fd',
          color: '#333',
          fontSize: 13,
          textTransform: 'none',
          padding: '11px 14px',
          borderBottom: '1px solid #dbe7f0',
          whiteSpace: 'nowrap',
        },
        // .data-table tbody td
        body: {
          fontSize: 13.5,
          padding: '13px 14px',
          borderBottom: '1px solid #eef2f4',
          // Row-action icon buttons get a tinted rounded box (wireframe
          // .btn-icon-sm / .act-view / .act-del) instead of a bare glyph.
          // Space consecutive row-action buttons so their boxes don't touch.
          '& .MuiIconButton-sizeSmall + .MuiIconButton-sizeSmall': { marginLeft: 6 },
          '& .MuiIconButton-sizeSmall': {
            width: 30,
            height: 30,
            borderRadius: 8,
            border: '1px solid #e4e9ef',
            color: '#6b7683',
            transition: 'background-color 140ms, border-color 140ms, color 140ms',
            '& .MuiSvgIcon-root': { fontSize: 17 },
            '&:hover': { backgroundColor: '#eaf3fc', borderColor: '#108ae6', color: '#108ae6' },
            // Neutral grey box for EVERY action at rest; semantic colour only
            // on hover (delete red, disable amber, activate green).
            '&.MuiIconButton-colorError': { color: '#6b7683', borderColor: '#e4e9ef' },
            '&.MuiIconButton-colorError:hover': { backgroundColor: '#fdecea', borderColor: '#dc5640', color: '#dc5640' },
            '&.MuiIconButton-colorSuccess': { color: '#6b7683', borderColor: '#e4e9ef' },
            '&.MuiIconButton-colorSuccess:hover': { backgroundColor: '#e7f7ee', borderColor: '#1a9c63', color: '#1a9c63' },
            '&.MuiIconButton-colorWarning': { color: '#6b7683', borderColor: '#e4e9ef' },
            '&.MuiIconButton-colorWarning:hover': { backgroundColor: '#fdf4e1', borderColor: '#c8891a', color: '#c8891a' },
            '&.Mui-disabled': { opacity: 0.4 },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8, transition: 'background-color 140ms, color 140ms' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        // Modern compact toggle — slim rounded track, clean thumb, brand blue
        // when on. Deliberately identical at both sizes: `size="small"` (used
        // in table Status columns) otherwise shrinks the thumb to 16px, which
        // then sits off-centre in this track. Neutralise it for uniformity.
        root: {
          width: 40, height: 22, padding: 0, marginRight: 6, overflow: 'visible',
          '&.MuiSwitch-sizeSmall': { width: 40, height: 22 },
          '&.MuiSwitch-sizeSmall .MuiSwitch-thumb': { width: 18, height: 18 },
          '&.MuiSwitch-sizeSmall .MuiSwitch-switchBase': { padding: 0, margin: '2px' },
          '&.MuiSwitch-sizeSmall .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(18px)' },
        },
        switchBase: {
          padding: 0,
          margin: '2px',
          transitionDuration: '180ms',
          color: '#fff',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': { backgroundColor: '#108ae6', opacity: 1, border: 0 },
          },
          // Disabled: muted but legible (reads as "locked", not broken/faded).
          // Full track opacity with softened colours + a dimmer thumb, instead
          // of washing the whole control to near-transparent.
          '&.Mui-disabled': {
            color: '#f2f5f8',
            '& + .MuiSwitch-track': { opacity: 1, backgroundColor: '#dce2e8' },
          },
          '&.Mui-disabled.Mui-checked + .MuiSwitch-track': { opacity: 1, backgroundColor: '#a6ccef' },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 18, height: 18,
          boxShadow: '0 1px 3px rgba(24,40,72,0.28)',
          '.Mui-disabled &': { boxShadow: '0 1px 2px rgba(24,40,72,0.12)' },
        },
        track: {
          borderRadius: 11,
          backgroundColor: '#c7d0da',
          opacity: 1,
          transition: 'background-color 180ms',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        // .data-table tbody tr:hover td
        root: { '&:hover > .MuiTableCell-body': { background: '#fafcff' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        // Lato's cap-height sits high in the box, so the label reads slightly
        // above centre. Flex-centre the label and let line-height match the
        // chip so status pills (CALCULATED, APPLIED, …) sit dead centre.
        root: { fontSize: 11, height: 22, fontWeight: 600, alignItems: 'center' },
        sizeSmall: { fontSize: 10.5, height: 20 },
        label: { lineHeight: 1, display: 'flex', alignItems: 'center', paddingTop: '1px' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 6, fontSize: 12.5 },
      },
    },
    // DataGrids don't inherit the MuiTableCell action styling above, so mirror
    // it here for the `actions` column — every grid's row actions match the
    // plain-table ones: semantic tinted boxes (delete red, disable amber,
    // activate green; view/edit neutral → blue on hover). NOTE: MUI X does not
    // always consume this theme override reliably, so <DataTable> also applies
    // the same block via its own sx (ACTION_ICON_SX) — keep the two in sync.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-cell[data-field="actions"] .MuiIconButton-root': {
            width: 30, height: 30, borderRadius: 8,
            border: '1px solid #e4e9ef', color: '#6b7683',
            transition: 'background-color 140ms, border-color 140ms, color 140ms',
            '& .MuiSvgIcon-root': { fontSize: 17 },
            '&:hover': { backgroundColor: '#eaf3fc', borderColor: '#108ae6', color: '#108ae6' },
            '&.MuiIconButton-colorError': { color: '#6b7683', borderColor: '#e4e9ef' },
            '&.MuiIconButton-colorError:hover': { backgroundColor: '#fdecea', borderColor: '#dc5640', color: '#dc5640' },
            '&.MuiIconButton-colorSuccess': { color: '#6b7683', borderColor: '#e4e9ef' },
            '&.MuiIconButton-colorSuccess:hover': { backgroundColor: '#e7f7ee', borderColor: '#1a9c63', color: '#1a9c63' },
            '&.MuiIconButton-colorWarning': { color: '#6b7683', borderColor: '#e4e9ef' },
            '&.MuiIconButton-colorWarning:hover': { backgroundColor: '#fdf4e1', borderColor: '#c8891a', color: '#c8891a' },
            '&.Mui-disabled': { opacity: 0.4 },
          },
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
